import { SelectItem } from 'primeng/api';
import { DatePipe, DecimalPipe } from '@angular/common';
import { INgxResult } from '../_interfaces/iresult';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

export class NgxUtility {

    // clone object
    static clone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj)) as T;
    }

    static hasProperty(obj: Object, property: string) {
        return obj && obj.hasOwnProperty(property);
    }

    static hasPropertyValue(obj: Object, property: string) {
        return obj && obj.hasOwnProperty(property) && obj[property];
    }

    static toSelectItems<T>(items: T[], valueFeild: string, labelFeild: string): SelectItem[] {
        const result: SelectItem[] = [];
        if (items && items.length > 0) {
            items.forEach(item => {
                result.push({
                    label: item[labelFeild],
                    value: item[valueFeild]
                });
            });
        }
        return result;
    }

    static toSelectItemsFormat<T>(items: T[], valueFeild: string, labelFeilds: string[], startSeprator: string = '<', endSeperator: string = '>'): SelectItem[] {
        const result: SelectItem[] = [];
        if (items && items.length > 0) {
            items.forEach(item => {
                result.push({
                    label: `${labelFeilds[0]}${startSeprator}${labelFeilds[1]}${endSeperator}`,
                    value: item[valueFeild]
                });
            });
        }
        return result;
    }

    static scrollToTop() {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }

    static scrollTo(elementId: string) {
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth'
            })
        };
    }

    static toFloat(value: any, defValue = 0): number {
        const v = parseFloat(value);
        if (isNaN(v)) {
            return defValue;
        }
        return v;
    }

    static toFloatExact(value: number): number {
        return NgxUtility.toFloat(value.toFixed(10));
    }

    static toInt(value: any, defValue = 0): number {
        const v = parseInt(value, 10);
        if (isNaN(v)) {
            return defValue;
        }
        return v;
    }

    static trim(obj: any) {
        if (obj) {
            Object.keys(obj).map(k => obj[k] = typeof obj[k] == 'string' ? obj[k].trim() : obj[k]);
        }
    }

    static getUniqueString(): string {
        return `${new Date().getTime()}_${Math.floor((Math.random() * 9999999) + 1)}`;
    }

    static jsonToDate(jsonStr: any): Date {
        if (jsonStr) {
            // get dt object
            const dt: Date = new Date(jsonStr);
            //NUll check
            return NgxUtility.isValidDate(dt) ? dt : null;
        }
        return null;
    }

    static isValidDate(date: Date): boolean {
        //NUll check
        if (date) {
            return isNaN(date.getTime()) ? false : true;
        }
    }

    static localToUtc(date: Date): Date {
        if (NgxUtility.isValidDate(date)) {
            return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
        }
        return;
    }

    static utcToLocal(date: Date): Date {
        if (NgxUtility.isValidDate(date)) {
            return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());
        }
        return null
    }

    static toDateFormat(dtStr: string, format: string, timeZone = undefined) {
        // get date
        const dt = NgxUtility.jsonToDate(dtStr);
        if (dt) {
            const datePipe = new DatePipe('en-US');
            return datePipe.transform(dt, format, timeZone);
        }
        return null;
    }

    static toAmountFormat(amount: any, currency: string = '') {
        if (amount) {
            const decimalPipe = new DecimalPipe('en-US');
            return `${currency}${decimalPipe.transform(amount, '1.2-2')}`;
        }
        return null;
    }

    static focusElement(elementId: string) {
        const ele: HTMLInputElement = document.getElementById(elementId) as HTMLInputElement;
        if (ele) {
            ele.focus();
            ele.select();
        }
    }

    static dialogContentStyle(maxHeight: string = '80vh'): any {
        return { "max-height": maxHeight, "overflow-y": "auto", "overflow-x": "hidden" };
    }

    static datePickerToDate(value: string, splitChar: string = '-'): Date {
        if (value) {
            const values = value.split(splitChar);
            // dd-mm-yy - 0-1-2
            return new Date(NgxUtility.toInt(values[2]), NgxUtility.toInt(values[1]) - 1, NgxUtility.toInt(values[0]))
        }
        return null;
    }

    static monthPickerToDate(value: string, splitChar: string = '-'): Date {
        if (value) {
            const values = value.split(splitChar);
            // dd-mm-yy - 0-1-2
            return new Date(NgxUtility.toInt(values[2]), NgxUtility.toInt(values[1]) - 1, 1)
        }
        return null;
    }

    static isSuccessResult(result: INgxResult): boolean {
        return NgxUtility.toInt(result.status) === 1 ? true : false;
    }

    static patchFormValue(value: Object, formGroup: FormGroup) {
        if (value) {
            Object.keys(formGroup.controls).forEach(controlName => {
                if (value.hasOwnProperty(controlName)) {
                    const data = value[controlName];
                    formGroup.controls[controlName].setValue(data);
                }
            });
        }
    }

    static cleanNullableObject(obj: any) {
        Object.keys(obj).forEach(key => {
            if (obj[key] && typeof obj[key] === "object") NgxUtility.cleanNullableObject(obj[key]); // recurse
            else if (obj[key] == null) delete obj[key]; // delete
        });
    }

    static getRouteKey(key: string, activatedRoute: ActivatedRoute): string {
        if (activatedRoute.snapshot.paramMap.has(key)) {
            return activatedRoute.snapshot.paramMap.get(key);
        }
        else {
            let value: string = null;
            let parent = activatedRoute.parent;
            let foundKey: boolean = false;
            while (!foundKey) {
                if (parent) {
                    if (parent.snapshot.paramMap.has(key)) {
                        value = parent.snapshot.paramMap.get(key);
                        foundKey = true;
                    }
                    else {
                        parent = parent.parent;
                    }
                }
                else {
                    foundKey = true;
                }
            }
            return value;
        }
    }

    static getRouteData(key: string, activatedRoute: ActivatedRoute): any {
        if (NgxUtility.hasProperty(activatedRoute.snapshot.data, key)) {
            return activatedRoute.snapshot.data[key];
        }
        else {
            let value: any = null;
            let parent = activatedRoute.parent;
            let foundKey: boolean = false;
            while (!foundKey) {
                if (parent) {
                    if (NgxUtility.hasProperty(parent.snapshot.data, key)) {
                        value = parent.snapshot.data[key];
                        foundKey = true;
                    }
                    else {
                        parent = parent.parent;
                    }
                }
                else {
                    foundKey = true;
                }
            }
            return value;
        }
    }
}